import type { IconName } from '@insio/ui-toolkit';
import { Permission } from '@insio/shared-types';

/**
 * Shared prompt and capability keys of the AI chat. Kept in one place so the home
 * page rails and the modals they mirror can never drift apart.
 */

export interface QuickActionPrompt {
  /** Key under `features.aiChat.quickActions`. */
  readonly key: string;
  /**
   * Incomplete on its own — prefills the composer so the user can name the supplier,
   * invoice or period before sending.
   */
  readonly needsInput?: boolean;
  /**
   * Backed by an admin-gated tool, so it is only offered to users who can actually
   * run it — a prompt that always answers "you don't have permission" is noise.
   */
  readonly adminOnly?: boolean;
  /**
   * Offered only to a user holding at least one of these permissions — without them the
   * answer is about work that is not theirs to act on.
   */
  readonly requires?: readonly Permission[];
  /** About approvals or the work queue, which simplified-mode accounts handle in INSIO. */
  readonly hiddenInSimplifiedMode?: true;
}

/** Any one of these makes the user somebody a decision can wait on. */
export const APPROVE_PERMISSIONS: readonly Permission[] = [
  Permission.INVOICES_APPROVE,
  Permission.REQUISITIONS_APPROVE,
  Permission.PURCHASE_ORDERS_APPROVE
];

/**
 * Prompts under `features.aiChat.quickActions`, in the order shown by the picker.
 * Mirrors the assistant's real skills — see the ai-chat tool registry.
 */
export const QUICK_ACTION_PROMPTS: readonly QuickActionPrompt[] = [
  { key: 'myWork', requires: APPROVE_PERMISSIONS, hiddenInSimplifiedMode: true },
  { key: 'myRequests', hiddenInSimplifiedMode: true },
  { key: 'notifications' },
  { key: 'duePayments' },
  { key: 'overdue' },
  { key: 'invoiceIssues', requires: [Permission.INVOICES_EDIT] },
  { key: 'spend' },
  { key: 'report' },
  { key: 'matching', requires: [Permission.INVOICES_MATCH] },
  { key: 'purchaseOrders' },
  { key: 'invoiceLookup', needsInput: true },
  { key: 'partnerLookup', needsInput: true },
  { key: 'partnerVerify', needsInput: true },
  { key: 'gettingStarted' },
  { key: 'mailbox', adminOnly: true },
  { key: 'plan', adminOnly: true },
  { key: 'syncStatus', adminOnly: true }
];

export interface HomeTopicCard {
  /** Key under `features.aiChat.quickActions.topics` — the card's heading. */
  readonly topic: string;
  /** Icon shown next to the heading. */
  readonly icon: IconName;
  /** Prompt keys under `features.aiChat.quickActions`. */
  readonly prompts: readonly QuickActionPrompt[];
}

/**
 * Topic cards on the home rail — a few prompts each, enough to show what the assistant
 * is for without turning the page into a manual. The full list stays in the picker.
 */
export const HOME_TOPIC_CARDS: readonly HomeTopicCard[] = [
  {
    topic: 'daily',
    icon: 'clock',
    prompts: [
      { key: 'myWork', requires: APPROVE_PERMISSIONS, hiddenInSimplifiedMode: true },
      { key: 'myRequests', hiddenInSimplifiedMode: true },
      { key: 'notifications' }
    ]
  },
  {
    topic: 'invoices',
    icon: 'file-invoice-dollar',
    prompts: [
      { key: 'overdue' },
      { key: 'invoiceIssues', requires: [Permission.INVOICES_EDIT] },
      { key: 'invoiceLookup', needsInput: true }
    ]
  },
  {
    topic: 'reporting',
    icon: 'coins',
    prompts: [{ key: 'duePayments' }, { key: 'spend' }, { key: 'report' }]
  },
  {
    topic: 'partners',
    icon: 'building',
    prompts: [
      { key: 'partnerLookup', needsInput: true },
      { key: 'partnerVerify', needsInput: true }
    ]
  }
];

/** Capability keys under `features.aiChat.help.capabilities`, in the order shown. */
export const CAPABILITY_KEYS = [
  'invoices',
  'validation',
  'approvals',
  'payments',
  'banking',
  'spend',
  'matching',
  'orders',
  'partners',
  'notifications',
  'account',
  'gettingStarted'
] as const;

/** Capabilities about approvals or the work queue, left out in simplified mode. */
export const SIMPLIFIED_MODE_HIDDEN_CAPABILITY_KEYS: readonly (typeof CAPABILITY_KEYS)[number][] = [
  'approvals',
  'notifications'
];

/** The few capabilities the home rail teases — the rest live in the full guide. */
export const HOME_RAIL_CAPABILITY_KEYS = ['invoices', 'payments', 'spend', 'matching'] as const;

/** Capability keys under `features.aiChat.help.admin` — admin-gated tools only. */
export const ADMIN_CAPABILITY_KEYS = [
  'mailbox',
  'plan',
  'usage',
  'settings',
  'sync',
  'users'
] as const;

/** Tip keys under `features.aiChat.help.tips`, in the order shown. */
export const TIP_KEYS = ['beSpecific', 'followUp', 'confirm', 'reset'] as const;

/** Limitation keys under `features.aiChat.help.limits`, in the order shown. */
export const LIMIT_KEYS = ['permissions', 'accuracy'] as const;
